/**
 * The Oqto area catalogue — how the API's resources group into permissions a
 * user can reason about, which scopes each area needs, and which resource
 * each request path belongs to.
 *
 * SCOPES ARE FORTNOX-SHAPED: one per resource, no read/write split. The
 * OpenAPI document does not list them; the developer portal does (28,
 * transcribed here from it on 2026-09-24, `spec-conformance.test.ts` pins
 * the count). Read-vs-write is therefore our own gate (`policy.ts`), as for
 * Fortnox.
 *
 * WHERE OQTO IS BETTER THAN FORTNOX: the access token is minted per company
 * with client credentials, and the mint takes a `scope` subset. So consent
 * asks for EVERY scope once (`OQTO_ALL_SCOPES`), and the token an agent's
 * call carries is minted with only the scopes of the areas the user
 * included (`resolveOqtoScopes`). Narrowing is enforced by Oqto on the
 * token as well as by us, and widening needs no re-consent — the company
 * already authorized everything, we simply mint a wider token. That is the
 * property `diffAreaSelection`, paired with `OQTO_ALL_SCOPES` as the grant,
 * expresses: nothing a user can pick ever reports a missing scope.
 *
 * Every area is offered: there is no per-area licence on Oqto's side. Should
 * one ever need withholding (the way Bokio's bank payments are), add a
 * `withheld` reason to the spec then — not before.
 */

import { parseAreaPermissions, type AreaPermissions } from "../integrations/area-permissions";

export interface OqtoAreaSpec {
  /** Stable key we store and show in the UI. */
  readonly key: string;
  /** Swedish label, matching what an Oqto user sees in their own product. */
  readonly label: string;
  readonly description: string;
  /** Whether "write" can be chosen. False for the company record, which
   *  identifies the connection and is never edited by an agent. */
  readonly canWrite: boolean;
  /** The portal's scope names this area needs — the ones a token minted for
   *  a connection that includes the area carries. */
  readonly scopes: readonly string[];
}

/**
 * Always covered, for the same reason as Fortnox's `companyinformation`: the
 * connection is named from `/organization`, and a bureau's fortieth
 * connection is indistinguishable from its first without it. Read-only —
 * `PUT /organization` exists, and no agent has a reason to change a
 * company's registered details.
 */
export const OQTO_ALWAYS_AREA = "organization";

export const OQTO_AREAS: readonly OqtoAreaSpec[] = [
  {
    key: "organization",
    label: "Företagsinformation",
    description:
      "Företagets namn, organisationsnummer och adress. Ingår alltid — utan den kan vi inte " +
      "veta vilket företag en koppling gäller.",
    canWrite: false,
    scopes: ["organization"],
  },
  {
    key: "accounting",
    label: "Bokföring",
    description:
      "Verifikationer och verifikationsserier, kontoplan, konteringsmallar, periodiseringar, " +
      "räkenskapsår och SIE-export/import.",
    canWrite: true,
    scopes: ["voucher", "voucher_serie", "account", "accounting_template", "accrual", "fiscal_year", "sie"],
  },
  {
    key: "customerinvoices",
    label: "Kundfakturor",
    description:
      "Kundfakturor, kreditfakturor, återkommande fakturor och deras betalningar. Skriv " +
      "omfattar även att bokföra och skicka fakturor.",
    canWrite: true,
    scopes: ["customer_invoice", "customer_invoice_credit", "customer_invoice_payment", "recurring_invoice"],
  },
  {
    key: "customers",
    label: "Kunder",
    description: "Kundregistret.",
    canWrite: true,
    scopes: ["customer"],
  },
  {
    key: "articles",
    label: "Artiklar",
    description: "Artiklar och artikelenheter.",
    canWrite: true,
    scopes: ["article", "article_unit"],
  },
  {
    key: "supplierinvoices",
    label: "Leverantörsfakturor",
    description:
      "Leverantörsfakturor, deras kreditfakturor och betalningar, samt attestflödet. Skriv " +
      "omfattar även att bokföra fakturor.",
    canWrite: true,
    scopes: ["supplier_invoice", "supplier_invoice_credit", "supplier_invoice_payment"],
  },
  {
    key: "suppliers",
    label: "Leverantörer",
    description: "Leverantörsregistret.",
    canWrite: true,
    scopes: ["supplier"],
  },
  {
    key: "inbox",
    label: "Inkorg",
    description: "Inkomna underlag som väntar på att registreras.",
    canWrite: true,
    scopes: ["inbox"],
  },
  {
    key: "attachments",
    label: "Bilagor",
    description: "Uppladdade filer och deras koppling till fakturor och verifikationer.",
    canWrite: true,
    scopes: ["attachment"],
  },
  {
    key: "fixedassets",
    label: "Anläggningsregister",
    description: "Anläggningstillgångar, tillgångstyper och avskrivningar.",
    canWrite: true,
    scopes: ["fixed_asset", "fixed_asset_type"],
  },
  {
    key: "projects",
    label: "Projekt",
    description: "Projekt som dimension på bokföringen.",
    canWrite: true,
    scopes: ["project"],
  },
  {
    key: "dimensions",
    label: "Dimensioner och taggar",
    description: "Kostnadsställen och andra dimensioner, samt taggar på poster.",
    canWrite: true,
    scopes: ["dimension", "tag"],
  },
  {
    key: "settings",
    label: "Inställningar",
    description: "Betalningsvillkor, valutor och momskoder. Sällan något en agent behöver ändra.",
    canWrite: true,
    // Currencies and the VAT field/rate/zone lookups have no scope of their
    // own in the portal; they ride on whatever token is minted.
    scopes: ["payment_term", "vat_code"],
  },
];

/** Every scope the portal offers — what consent asks for, once. */
export const OQTO_ALL_SCOPES: readonly string[] =
  [...new Set(OQTO_AREAS.flatMap((a) => a.scopes))].sort();

export const areaByKey = (key: string): OqtoAreaSpec | undefined =>
  OQTO_AREAS.find((a) => a.key === key);

/**
 * Path resource segment -> area key. One row per tag in the published spec;
 * `spec-conformance.test.ts` checks the two agree in both directions.
 *
 * Sub-resources (`/customerinvoices/{id}/payments`, `/supplierinvoices/{id}/
 * credits`, `/{resource}/{id}/files`) belong to their parent's area: a
 * payment against an invoice is invoice work.
 */
export const OQTO_AREA_BY_RESOURCE: Readonly<Record<string, string>> = {
  organization: "organization",

  accountingtemplates: "accounting",
  accounts: "accounting",
  accruals: "accounting",
  fiscalyears: "accounting",
  sie: "accounting",
  vouchers: "accounting",
  voucherseries: "accounting",

  customerinvoices: "customerinvoices",
  customerinvoicecredits: "customerinvoices",
  recurringinvoices: "customerinvoices",

  customers: "customers",

  articles: "articles",
  articleunits: "articles",

  supplierinvoices: "supplierinvoices",
  approvals: "supplierinvoices",

  suppliers: "suppliers",

  inbox: "inbox",

  attachments: "attachments",

  fixedassets: "fixedassets",
  fixedassettypes: "fixedassets",

  projects: "projects",

  dimensions: "dimensions",
  tags: "dimensions",

  currencies: "settings",
  paymentterms: "settings",
  vatcodes: "settings",
  vatfields: "settings",
  vatrates: "settings",
  vatzones: "settings",
};

/** The Company API prefix every path carries on the wire. */
export const OQTO_API_PREFIX = "/company/v1";

/**
 * Split a path into its non-empty segments, ignoring any query string and
 * the `/company/v1` prefix if the caller included it. The one place an Oqto
 * path is decomposed: both halves of the gate read it, and a boundary whose
 * halves disagree about where the resource starts is how a gate is bypassed.
 */
export const oqtoSegmentsOf = (path: string): readonly string[] => {
  const seg = path.split("?")[0].split("/").filter((s) => s.length > 0);
  return seg[0] === "company" && seg[1] === "v1" ? seg.slice(2) : seg;
};

/** The first segment, which is the resource. */
export const oqtoResourceOf = (path: string): string =>
  (oqtoSegmentsOf(path)[0] ?? "").toLowerCase();

/** The area a path belongs to, or null when we do not recognise it. Null is
 *  meaningful: the gate refuses on it. */
export const oqtoAreaForPath = (path: string): OqtoAreaSpec | null => {
  const key = OQTO_AREA_BY_RESOURCE[oqtoResourceOf(path)];
  return key === undefined ? null : areaByKey(key) ?? null;
};

/** The area keys a selection covers: the company record plus every known
 *  key in it. An unknown key contributes nothing. */
export const resolveOqtoAreas = (selection: AreaPermissions): readonly string[] =>
  [...new Set([
    OQTO_ALWAYS_AREA,
    ...Object.keys(selection).filter((key) => areaByKey(key) !== undefined),
  ])].sort();

/**
 * The scopes a selection needs — what goes in `granted_scopes` and what the
 * runtime mints the company's token with. This is the `requiredScopes`
 * function `diffAreaSelection` takes; against `OQTO_ALL_SCOPES` it never
 * reports a missing scope, because consent asked for all of them.
 */
export const resolveOqtoScopes = (selection: AreaPermissions): readonly string[] =>
  [...new Set(
    resolveOqtoAreas(selection).flatMap((key) => areaByKey(key)?.scopes ?? []),
  )].sort();

/**
 * The areas a scope set reaches: every area whose scopes are all held. The
 * inverse of `resolveOqtoScopes`, used wherever a stored connection is read
 * back — the runtime's area check and the settings page.
 */
export const oqtoAreasCoveredBy = (scopes: readonly string[]): readonly string[] => {
  const held = new Set(scopes);
  return OQTO_AREAS
    .filter((a) => a.scopes.every((s) => held.has(s)))
    .map((a) => a.key)
    .sort();
};

/**
 * Read an area selection off a request body, or say why it cannot be used.
 *
 * The three web routes that take one (authorize, manual connect, change)
 * apply the same three rules, so they are here rather than in each:
 *
 *  * an area this integration does not offer is refused by name rather than
 *    dropped — dropping it would produce a connection missing the thing the
 *    user just asked for, with nothing saying why;
 *  * the company record can never be set to write, whatever the browser sent;
 *  * the company record alone is not a usable connection. Disconnecting is
 *    the way to hold nothing.
 */
export const validateOqtoSelection = (
  value: unknown,
): { readonly ok: true; readonly selection: AreaPermissions } | { readonly ok: false; readonly error: string } => {
  const selection = parseAreaPermissions(value);
  const unknown = Object.keys(selection).filter((key) => areaByKey(key) === undefined);
  if (unknown.length > 0) return { ok: false, error: `Not areas this integration offers: ${unknown.join(", ")}` };
  if (selection[OQTO_ALWAYS_AREA] === "write") return { ok: false, error: `"${OQTO_ALWAYS_AREA}" is read-only` };
  if (resolveOqtoAreas(selection).every((a) => a === OQTO_ALWAYS_AREA)) {
    return { ok: false, error: "Pick at least one Oqto area, or disconnect the company" };
  }
  return { ok: true, selection };
};
